import classNames from "classnames/bind";
import React, { forwardRef } from "react";
import { Skeleton } from "../Skeleton";
import { getIcon } from "./Button.helpers";
import styles from "./Button.module.scss";
import { ButtonProps } from "./types";

const cx = classNames.bind(styles);

/**
 * Buttons are used to initialize an action. Button labels express what action
 * will occur when the user interacts with it
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className = "",
      style,
      variant = "outline",
      size = "small",
      type = "button",
      icon,
      slotLeft,
      slotRight,
      onClick,
      disabled,
      iconPosition = "left",
      iconProps = {},
      isLoading = false,
      isBusy = false,
      as: Component = "button",
      children,
      ...props
    }: ButtonProps,
    ref
  ) => {
    const variantClassName =
      (
        {
          primary: "button--primary",
          error: "button--error",
          "error-outline": "button--error-outline",
          "warning-outline": "button--warning-outline",
          text: "button--text",
          "text-error": "button--text-error",
          ghost: "button--text-ghost",
          outline: "button--outline",
          special: "button--special",
        } satisfies Record<Exclude<ButtonProps["variant"], undefined>, string>
      )[variant] ?? "button--outline";
    const buttonIcon = getIcon(icon, {
      isBusy: isLoading || isBusy, // IMPROVE: map only to isBusy when isLoading is wired up to skeleton
      iconComponentProps: iconProps,
    });

    return (
      <Skeleton
        // IMPROVE: wire this up when ready to make all downstream changes to split isLoading/isBusy intents
        // isLoading={isLoading}
        isBusy={isBusy}
      >
        <Component
          className={cx("button", size && `button--${size}`, variantClassName, {
            iconOnly: !children && icon,
            [className]: className,
          })}
          onClick={onClick}
          disabled={isLoading || isBusy || disabled}
          style={style}
          type={type}
          {...props}
          ref={ref}
        >
          <>
            {slotLeft ? slotLeft : iconPosition === "left" && buttonIcon}

            {children ? (
              <span
                className={cx("buttonContent")}
                data-content
              >
                {children}
              </span>
            ) : null}

            {slotRight ? slotRight : iconPosition === "right" && buttonIcon}
          </>
        </Component>
      </Skeleton>
    );
  }
);

Button.displayName = "Button";

export default Button;
